import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Compass,
  ExternalLink,
  Plus,
  ToggleLeft,
  ToggleRight,
} from 'lucide-react';
import {
  createAdminNavigation,
  fetchAdminNavigation,
  fetchAdminNavigationItem,
  patchAdminNavigation,
  type AdminNavigationRow,
} from '../../api/admin';
import { ApiError } from '../../api/client';
import {
  AdminDetailCard,
  AdminEmptyState,
  AdminKpiStrip,
  AdminPageLayout,
  AdminSearchInput,
  AdminStatusBadge,
  AdminTablePanel,
} from '../../components/admin/AdminPageLayout';
import { resolveIcon } from '../../lib/iconMap';
import { LoadingState, ErrorState } from '../../components/states/StateViews';

const NAV_ICON_OPTIONS = [
  'Home',
  'Trophy',
  'Briefcase',
  'ClipboardList',
  'FolderLock',
  'Calendar',
  'LayoutDashboard',
  'Bell',
  'Settings',
  'UserCircle',
  'Compass',
  'Newspaper',
  'GraduationCap',
  'Search',
];

function permissionLabel(permission: string) {
  if (permission === 'authenticated') return 'Signed-in users';
  return 'Public';
}

function CreateNavItemModal({
  open,
  onClose,
  nextOrder,
}: {
  open: boolean;
  onClose: () => void;
  nextOrder: number;
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    title: '',
    shortTitle: '',
    route: '',
    icon: 'Home',
    displayOrder: nextOrder,
    permission: 'public',
    context: 'primary',
  });
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setForm({
      title: '',
      shortTitle: '',
      route: '',
      icon: 'Home',
      displayOrder: nextOrder,
      permission: 'public',
      context: 'primary',
    });
    setError('');
  }, [open, nextOrder]);

  const create = useMutation({
    mutationFn: () =>
      createAdminNavigation({
        title: form.title.trim(),
        shortTitle: form.shortTitle.trim(),
        route: form.route.trim(),
        icon: form.icon,
        displayOrder: form.displayOrder,
        permission: form.permission,
        context: form.context,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-navigation'] });
      onClose();
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : 'Failed to create navigation item');
    },
  });

  if (!open) return null;

  return (
    <div className="wfx-admin-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="wfx-admin-modal glass-surface"
        role="dialog"
        aria-labelledby="create-nav-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="wfx-admin-modal-head">
          <div>
            <p className="wfx-admin-breadcrumb">Site navigation</p>
            <h2 id="create-nav-title"><Compass size={20} /> New nav item</h2>
          </div>
        </header>
        <form
          className="wfx-admin-form"
          onSubmit={(e) => {
            e.preventDefault();
            setError('');
            create.mutate();
          }}
        >
          <label className="wfx-admin-field">
            <span>Title</span>
            <input
              required
              value={form.title}
              onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
            />
          </label>
          <div className="wfx-admin-form-row">
            <label className="wfx-admin-field">
              <span>Short title (dock)</span>
              <input
                required
                value={form.shortTitle}
                onChange={(e) => setForm((f) => ({ ...f, shortTitle: e.target.value }))}
              />
            </label>
            <label className="wfx-admin-field">
              <span>Display order</span>
              <input
                type="number"
                value={form.displayOrder}
                onChange={(e) => setForm((f) => ({ ...f, displayOrder: Number(e.target.value) || 0 }))}
              />
            </label>
          </div>
          <label className="wfx-admin-field">
            <span>Route</span>
            <input
              required
              placeholder="/results"
              value={form.route}
              onChange={(e) => setForm((f) => ({ ...f, route: e.target.value }))}
            />
          </label>
          <div className="wfx-admin-form-row">
            <label className="wfx-admin-field">
              <span>Icon</span>
              <select value={form.icon} onChange={(e) => setForm((f) => ({ ...f, icon: e.target.value }))}>
                {NAV_ICON_OPTIONS.map((icon) => (
                  <option key={icon} value={icon}>{icon}</option>
                ))}
              </select>
            </label>
            <label className="wfx-admin-field">
              <span>Permission</span>
              <select
                value={form.permission}
                onChange={(e) => setForm((f) => ({ ...f, permission: e.target.value }))}
              >
                <option value="public">Public</option>
                <option value="authenticated">Signed-in users</option>
              </select>
            </label>
          </div>
          <label className="wfx-admin-field">
            <span>Context</span>
            <input
              value={form.context}
              onChange={(e) => setForm((f) => ({ ...f, context: e.target.value }))}
            />
          </label>
          {error && <p className="wfx-admin-form-error">{error}</p>}
          <div className="wfx-admin-modal-actions">
            <button type="button" className="secondary-action" onClick={onClose}>Cancel</button>
            <button type="submit" className="primary-action" disabled={create.isPending}>
              {create.isPending ? 'Creating…' : 'Add nav item'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function NavItemDetailCard({
  itemId,
  onClose,
}: {
  itemId: string | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin-navigation-item', itemId],
    queryFn: () => fetchAdminNavigationItem(itemId!),
    enabled: Boolean(itemId),
  });

  const update = useMutation({
    mutationFn: (body: Partial<AdminNavigationRow>) => patchAdminNavigation(itemId!, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-navigation'] });
      void qc.invalidateQueries({ queryKey: ['admin-navigation-item', itemId] });
      void qc.invalidateQueries({ queryKey: ['navigation'] });
    },
  });

  const item = data?.item;
  const Icon = resolveIcon(item?.icon);

  return (
    <AdminDetailCard
      open={Boolean(itemId)}
      onClose={onClose}
      ariaLabel="Navigation item details"
      breadcrumb="Nav item"
      title={item?.title ?? 'Loading…'}
      illustration="admin.module.cms"
      subtitle={
        item ? (
          <>
            <AdminStatusBadge status={item.visible ? 'visible' : 'hidden'} />
            <span className="wfx-muted wfx-admin-subline">{item.context} · order {item.displayOrder}</span>
          </>
        ) : undefined
      }
    >
      {isLoading && <LoadingState label="Loading nav item…" />}
      {isError && <ErrorState message="Could not load nav item." onRetry={() => void refetch()} />}

      {item && (
        <>
          <dl className="wfx-admin-meta-list wfx-admin-meta-list--grid">
            <div><dt>ID</dt><dd>{item.id}</dd></div>
            <div><dt>Context</dt><dd>{item.context}</dd></div>
            <div><dt>Permission</dt><dd>{permissionLabel(item.permission)}</dd></div>
            <div>
              <dt>Route</dt>
              <dd>
                <Link to={item.route} className="secondary-action compact" target="_blank">
                  {item.route}
                  <ExternalLink size={12} aria-hidden="true" />
                </Link>
              </dd>
            </div>
          </dl>

          <div className="wfx-admin-nav-dock-preview glass-surface" aria-label="Dock preview">
            <span className="wfx-muted wfx-admin-nav-dock-label">Dock preview</span>
            <div className="wfx-admin-nav-dock-items">
              <div className="wfx-admin-nav-dock-chip">
                <Icon size={18} aria-hidden="true" />
                <span>{item.shortTitle}</span>
              </div>
            </div>
          </div>

          <div className="wfx-admin-drawer-columns">
            <div className="wfx-admin-drawer-section">
              <h3>Edit labels</h3>
              <div className="wfx-admin-form">
                <label className="wfx-admin-field">
                  <span>Title</span>
                  <input
                    defaultValue={item.title}
                    onBlur={(e) => {
                      const value = e.target.value.trim();
                      if (value && value !== item.title) update.mutate({ title: value });
                    }}
                  />
                </label>
                <label className="wfx-admin-field">
                  <span>Short title</span>
                  <input
                    defaultValue={item.shortTitle}
                    onBlur={(e) => {
                      const value = e.target.value.trim();
                      if (value && value !== item.shortTitle) update.mutate({ shortTitle: value });
                    }}
                  />
                </label>
              </div>
            </div>

            <div className="wfx-admin-drawer-section">
              <h3>Routing & display</h3>
              <div className="wfx-admin-form">
                <label className="wfx-admin-field">
                  <span>Route</span>
                  <input
                    defaultValue={item.route}
                    onBlur={(e) => {
                      const value = e.target.value.trim();
                      if (value && value !== item.route) update.mutate({ route: value });
                    }}
                  />
                </label>
                <label className="wfx-admin-field">
                  <span>Icon</span>
                  <select
                    defaultValue={item.icon}
                    onChange={(e) => update.mutate({ icon: e.target.value })}
                  >
                    {NAV_ICON_OPTIONS.map((icon) => (
                      <option key={icon} value={icon}>{icon}</option>
                    ))}
                  </select>
                </label>
                <label className="wfx-admin-field">
                  <span>Display order</span>
                  <input
                    type="number"
                    defaultValue={item.displayOrder}
                    onBlur={(e) => {
                      const value = Number(e.target.value);
                      if (!Number.isNaN(value) && value !== item.displayOrder) {
                        update.mutate({ displayOrder: value });
                      }
                    }}
                  />
                </label>
                <label className="wfx-admin-field">
                  <span>Permission</span>
                  <select
                    defaultValue={item.permission}
                    onChange={(e) => update.mutate({ permission: e.target.value })}
                  >
                    <option value="public">Public</option>
                    <option value="authenticated">Signed-in users</option>
                  </select>
                </label>
                <button
                  type="button"
                  className="secondary-action compact"
                  disabled={update.isPending}
                  onClick={() => update.mutate({ visible: !item.visible })}
                >
                  {item.visible ? (
                    <><ToggleRight size={16} aria-hidden="true" /> Hide from site</>
                  ) : (
                    <><ToggleLeft size={16} aria-hidden="true" /> Show on site</>
                  )}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </AdminDetailCard>
  );
}

function NavItemCard({
  item,
  onSelect,
}: {
  item: AdminNavigationRow;
  onSelect: () => void;
}) {
  const Icon = resolveIcon(item.icon);

  return (
    <button type="button" className="wfx-admin-nav-item-card glass-surface" onClick={onSelect}>
      <div className="wfx-admin-nav-item-card-head">
        <span className="wfx-admin-nav-item-icon" aria-hidden="true">
          <Icon size={20} />
        </span>
        <AdminStatusBadge status={item.visible ? 'visible' : 'hidden'} />
      </div>
      <strong>{item.title}</strong>
      <span className="wfx-muted">{item.route}</span>
      <div className="wfx-admin-page-card-stats">
        <span>Order {item.displayOrder}</span>
        <span>{item.shortTitle}</span>
        <span>{permissionLabel(item.permission)}</span>
      </div>
    </button>
  );
}

function DockPreview({ items }: { items: AdminNavigationRow[] }) {
  const visibleItems = items.filter((item) => item.visible);

  return (
    <div className="wfx-admin-nav-dock-preview glass-surface" aria-label="Primary dock order preview">
      <span className="wfx-muted wfx-admin-nav-dock-label">Primary dock order (visible items)</span>
      <div className="wfx-admin-nav-dock-items">
        {visibleItems.map((item) => {
          const Icon = resolveIcon(item.icon);
          return (
            <div key={item.id} className="wfx-admin-nav-dock-chip">
              <Icon size={18} aria-hidden="true" />
              <span>{item.shortTitle}</span>
            </div>
          );
        })}
        {visibleItems.length === 0 && <span className="wfx-muted">No visible items in this context.</span>}
      </div>
    </div>
  );
}

export function AdminNavigationScreen() {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [contextFilter, setContextFilter] = useState('all');
  const [visibleFilter, setVisibleFilter] = useState('all');
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedQuery(query.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [query]);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin-navigation', debouncedQuery, contextFilter, visibleFilter],
    queryFn: () =>
      fetchAdminNavigation({
        q: debouncedQuery || undefined,
        context: contextFilter,
        visible: visibleFilter,
      }),
  });

  const rows = data?.data ?? [];
  const stats = data?.stats;
  const contexts = data?.contexts ?? ['primary'];
  const nextOrder = useMemo(
    () => (rows.length ? Math.max(...rows.map((row) => row.displayOrder)) + 1 : 1),
    [rows],
  );

  const kpiItems = useMemo(
    () => [
      { label: 'Total items', value: stats?.total ?? 0 },
      { label: 'Visible', value: stats?.visible ?? 0 },
      { label: 'Hidden', value: stats?.hidden ?? 0 },
      { label: 'Auth-gated', value: stats?.authenticated ?? 0 },
    ],
    [stats],
  );

  if (isLoading && !data) return <LoadingState label="Loading navigation…" />;
  if (isError) return <ErrorState message="Could not load navigation." onRetry={() => void refetch()} />;

  return (
    <>
      <AdminPageLayout
        breadcrumb="Admin / Content / Navigation"
        title="Navigation"
        description="Manage primary dock links, labels, routes, and visibility for the public site."
        icon={Compass}
        illustration="admin.module.cms"
        actions={(
          <button type="button" className="primary-action compact" onClick={() => setCreateOpen(true)}>
            <Plus size={16} aria-hidden="true" />
            New nav item
          </button>
        )}
      >
        <AdminKpiStrip items={kpiItems} />

        <DockPreview items={rows.filter((row) => contextFilter === 'all' || row.context === contextFilter)} />

        <div className="wfx-admin-faq-section-grid">
          {rows.map((item) => (
            <NavItemCard key={item.id} item={item} onSelect={() => setSelectedId(item.id)} />
          ))}
        </div>
        {rows.length === 0 && <AdminEmptyState message="No navigation items match your filters." />}

        <AdminTablePanel
          title="All navigation items"
          toolbar={(
            <div className="wfx-admin-toolbar">
              <AdminSearchInput
                value={query}
                onChange={setQuery}
                placeholder="Search title, route, or id…"
              />
              <select
                className="wfx-admin-select"
                value={contextFilter}
                onChange={(e) => setContextFilter(e.target.value)}
                aria-label="Filter by context"
              >
                <option value="all">All contexts</option>
                {contexts.map((ctx) => (
                  <option key={ctx} value={ctx}>{ctx}</option>
                ))}
              </select>
              <select
                className="wfx-admin-select"
                value={visibleFilter}
                onChange={(e) => setVisibleFilter(e.target.value)}
                aria-label="Filter by visibility"
              >
                <option value="all">All visibility</option>
                <option value="true">Visible only</option>
                <option value="false">Hidden only</option>
              </select>
            </div>
          )}
        >
          <table className="wfx-admin-table">
            <thead>
              <tr><th>Title</th><th>Route</th><th>Context</th><th>Order</th><th>Permission</th><th>Status</th></tr>
            </thead>
            <tbody>
              {rows.map((item) => (
                <tr
                  key={item.id}
                  className="wfx-admin-table-row-clickable"
                  onClick={() => setSelectedId(item.id)}
                >
                  <td><strong>{item.title}</strong></td>
                  <td>{item.route}</td>
                  <td>{item.context}</td>
                  <td>{item.displayOrder}</td>
                  <td>{permissionLabel(item.permission)}</td>
                  <td><AdminStatusBadge status={item.visible ? 'visible' : 'hidden'} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </AdminTablePanel>
      </AdminPageLayout>

      <CreateNavItemModal open={createOpen} onClose={() => setCreateOpen(false)} nextOrder={nextOrder} />
      <NavItemDetailCard itemId={selectedId} onClose={() => setSelectedId(null)} />
    </>
  );
}
